import {
    PensionStatus,
    PensionerDetails,
    TaxDetail,
    ParticipantDemographics,
    PensionDetails,
    PaymentDestination,
    PaymentDestinationTableData,
} from "@/interfaces/pension";
import { defineStore } from "pinia";
import { PensionMode } from '@/enums/PensionMode';
import axios, { AxiosError } from "axios";

export const usePensionStore = defineStore({
    id: "pension",
    state: () => ({
        data: null as PensionStatus | null,
        pensionerDetails: null as PensionerDetails | null,
        hasChildren: true as boolean,
        history: {
            headers: [
                { title: "Year" },
                { title: "Credited" },
                { title: "Worked" },
                { title: "Type" },
                { title: "Plan" },
                { title: "Vesting" },
                { title: "Pension" },
                { title: "$ Credited" },
                { title: "Comments" },
                { title: "PR" },
            ],
            rows: [],
        },

        paymentHistory: {
            headers: [
                { title: "Date" },
                { title: "Check#" },
                { title: "Gross" },
                { title: "Deductions" },
                { title: "Net" },
                { title: "YTD" },
                { title: "ClearDate" },
            ],
            rows: [],
            hasChildren: false,
        },
        paymentDestination: {
            headers: [
                { title: "Name" },
                { title: "Type" },
                { title: "Destination" },
            ],
            rows: [] as PaymentDestinationTableData[],
        },
        taxDetails: [] as TaxDetail[],
        pensionDetails: null as PensionDetails | null,
        demographics: null as ParticipantDemographics | null,
        creditsUnderMaintenance: false,
        creditsLoadFailed: false,
        pchStatus: null as string | null,
        mode: null as PensionMode | null,
    }),
    actions: {
        async fetchPensionCredits() {
            this.creditsUnderMaintenance = false;
            this.creditsLoadFailed = false;

            try {
                const response = await axios.get(
                    "api/participant/pension/credits"
                );

                this.mode = response.data.Mode ?? PensionMode.SUBMISSION;
                if (this.mode === PensionMode.SUBMISSION) return;
                this.pchStatus = response.data.PchStatus ?? null;
                this.data = response.data.GeneralInfo;
                this.hasChildren = response.data.HasChildren;
                this.history.rows = response.data.History;
            } catch (error: unknown) {
                const axiosError = error as AxiosError<{
                    message?: string;
                    code?: string;
                    Mode?: PensionMode;
                }>;

                if (
                    axiosError.response?.status === 503 &&
                    axiosError.response.data?.code ===
                        "PENSION_CREDITS_MAINTENANCE"
                ) {
                    this.creditsUnderMaintenance = true;
                    this.mode = axiosError.response.data?.Mode ?? PensionMode.SUBMISSION;

                    return;
                }

                this.creditsLoadFailed = true;
                console.error(
                    "Unable to load pension credits.",
                    axiosError
                );
            }
        },

        async fetchPaymentHistory() {
            await axios
                .get("api/participant/pension/payments")
                .then((response) => {
                    this.paymentHistory.rows = Object.values(
                        response.data.Payments
                    );
                    this.taxDetails = response.data.TaxDetails;
                    this.paymentHistory.hasChildren = response.data.HasChildren;
                })
                .catch((error) => {
                    console.error("There was an error!", error);
                });
        },

        async fetchDemographics() {
            await axios
                .get("api/participant/pension/demographics")
                .then((response) => {
                    this.demographics = response.data.Demographics;
                    this.pensionDetails = response.data.PensionDetails;
                    const paymentDestinations = Object.values(response.data.PaymentDestination) as PaymentDestination[];
                    this.paymentDestination.rows = paymentDestinations.flatMap((item: PaymentDestination) => item.TableData);
                })
                .catch((error) => {
                    console.error("There was an error!", error);
                });
        },

        async getPanelDetails() {
            await axios
                .get("api/participant/pension-panel")
                .then((response) => {
                    this.pensionerDetails = response.data;
                })
                .catch((error) => {
                    console.error("There was an error!", error);
                });
        },

        reset() {
            this.$reset();
        },
    },
});
